/**
 * SpryDrive File Picker for SpryBoard
 *
 * Provides UI for:
 * - Creating new boards
 * - Opening boards from SpryDrive
 * - Saving boards to SpryDrive
 * - Recent boards list
 */
import { useState, useEffect, useCallback } from "react";
import {
  boardAPI,
  spryboardAPI,
  extractBoardsFromResponse,
  type NormalizedBoardSummary,
} from "../api/backend";

import "./SpryDrivePicker.scss";

interface BoardFile {
  id: string;
  filename: string;
  size?: number;
  uploadDate?: string;
  starred?: boolean;
  boardId?: string;
  driveFileId?: string;
}

type BoardTarget = {
  boardId?: string;
  fileId?: string;
  title: string;
};

interface SpryDrivePickerProps {
  onOpen: (context: BoardTarget) => void;
  onCreateBoard: () => void;
  onClose: () => void;
  isOpen: boolean;
}

const safeDate = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString();
};

const toDisplayTitle = (value?: string) =>
  (value || "Untitled Board").replace(/\.spryboard$/i, "").trim() || "Untitled Board";

export const SpryDrivePicker = ({ onOpen, onCreateBoard, onClose, isOpen }: SpryDrivePickerProps) => {
  const [files, setFiles] = useState<BoardFile[]>([]);
  const [boards, setBoards] = useState<NormalizedBoardSummary[]>([]);
  const [sharedBoards, setSharedBoards] = useState<NormalizedBoardSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"recent" | "drive" | "shared">("recent");

  const loadFiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [filesRes, boardsRes] = await Promise.all([
        spryboardAPI.listFiles().catch(() => ({ data: { data: [] } })),
        boardAPI.getBoards().catch(() => ({ data: { boards: [] } })),
      ]);

      setFiles(filesRes.data?.data || []);
      setBoards(extractBoardsFromResponse(boardsRes));
    } catch (err) {
      setError("Failed to load files");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadShared = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await boardAPI.getSharedWithMe();
      setSharedBoards(extractBoardsFromResponse(res));
    } catch (err) {
      setError("Failed to load shared boards");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (tab === "shared") {
        loadShared();
      } else {
        loadFiles();
      }
    }
  }, [isOpen, tab, loadFiles, loadShared]);

  const handleOpenFile = (file: BoardFile) => {
    onOpen({
      fileId: file.id,
      title: toDisplayTitle(file.filename),
    });
  };

  const handleOpenBoard = (board: NormalizedBoardSummary) => {
    onOpen({
      boardId: board.id,
      fileId: board.driveFileId || undefined,
      title: toDisplayTitle(board.title),
    });
  };

  const handleNewBoard = () => {
    onCreateBoard();
  };

  const standaloneFiles = files.filter(
    (file) => !boards.some((board) => board.driveFileId && board.driveFileId === file.id),
  );

  const recentItems = [
    ...boards.map((board) => ({
      key: `board-${board.id}`,
      kind: "board" as const,
      title: toDisplayTitle(board.title),
      meta: safeDate(board.updatedAt || board.createdAt),
      sortTs: new Date(board.updatedAt || board.createdAt || 0).getTime() || 0,
      badge: "Board",
      onClick: () => handleOpenBoard(board),
    })),
    ...standaloneFiles.map((file) => ({
      key: `file-${file.id}`,
      kind: "file" as const,
      title: toDisplayTitle(file.filename),
      meta: safeDate(file.uploadDate),
      sortTs: new Date(file.uploadDate || 0).getTime() || 0,
      badge: "Drive file",
      onClick: () => handleOpenFile(file),
    })),
  ].sort((a, b) => {
    return b.sortTs - a.sortTs;
  });

  const retryCurrentTab = () => {
    if (tab === "shared") {
      void loadShared();
      return;
    }
    void loadFiles();
  };

  if (!isOpen) return null;

  return (
    <div className="spry-drive-picker-overlay" onClick={onClose}>
      <div className="spry-drive-picker" onClick={(e) => e.stopPropagation()}>
        <div className="spry-drive-picker__header">
          <h2>SpryBoard Files</h2>
          <button className="spry-drive-picker__close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="spry-drive-picker__tabs">
          <button
            className={`spry-drive-picker__tab ${tab === "recent" ? "active" : ""}`}
            onClick={() => setTab("recent")}
          >
            Recent
          </button>
          <button
            className={`spry-drive-picker__tab ${tab === "drive" ? "active" : ""}`}
            onClick={() => setTab("drive")}
          >
            SpryDrive
          </button>
          <button
            className={`spry-drive-picker__tab ${tab === "shared" ? "active" : ""}`}
            onClick={() => setTab("shared")}
          >
            Shared with me
          </button>
        </div>

        <div className="spry-drive-picker__content">
          <button className="spry-drive-picker__new-btn" onClick={handleNewBoard}>
            + New Board
          </button>

          {loading && <div className="spry-drive-picker__loading">Loading...</div>}

          {error && (
            <div className="spry-drive-picker__error">
              <div>{error}</div>
              <button
                type="button"
                className="spry-drive-picker__retry"
                onClick={retryCurrentTab}
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !error && tab === "recent" && (
            <div className="spry-drive-picker__list">
              {recentItems.length === 0 && (
                <div className="spry-drive-picker__empty">
                  No boards found. Create a new one!
                </div>
              )}
              {recentItems.map((item) => (
                <div
                  key={item.key}
                  className="spry-drive-picker__item"
                  onClick={item.onClick}
                >
                  <div
                    className={`spry-drive-picker__item-icon spry-drive-picker__item-icon--${item.kind}`}
                    aria-hidden="true"
                  >
                    <span />
                  </div>
                  <div className="spry-drive-picker__item-info">
                    <div className="spry-drive-picker__item-name">{item.title}</div>
                    <div className="spry-drive-picker__item-meta">
                      {item.meta || "Recently updated"}
                    </div>
                  </div>
                  <div className={`spry-drive-picker__source-badge spry-drive-picker__source-badge--${item.kind}`}>
                    {item.badge}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && !error && tab === "drive" && (
            <div className="spry-drive-picker__list">
              {files.length === 0 && (
                <div className="spry-drive-picker__empty">
                  No SpryBoard files found in SpryDrive.
                </div>
              )}
              {files.map((file) => (
                <div
                  key={`drive-${file.id}`}
                  className="spry-drive-picker__item"
                  onClick={() => handleOpenFile(file)}
                >
                  <div className="spry-drive-picker__item-icon spry-drive-picker__item-icon--file" aria-hidden="true">
                    <span />
                  </div>
                  <div className="spry-drive-picker__item-info">
                    <div className="spry-drive-picker__item-name">{toDisplayTitle(file.filename)}</div>
                    <div className="spry-drive-picker__item-meta">
                      {safeDate(file.uploadDate) || "SpryDrive file"}
                    </div>
                  </div>
                  <div className="spry-drive-picker__source-badge spry-drive-picker__source-badge--file">
                    Drive file
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && !error && tab === "shared" && (
            <div className="spry-drive-picker__list">
              {sharedBoards.length === 0 && (
                <div className="spry-drive-picker__empty">
                  No shared boards found.
                </div>
              )}
              {sharedBoards.map((board) => (
                <div
                  key={`shared-${board.id}`}
                  className="spry-drive-picker__item"
                  onClick={() => handleOpenBoard(board)}
                >
                  <div className="spry-drive-picker__item-icon spry-drive-picker__item-icon--shared" aria-hidden="true">
                    <span />
                  </div>
                  <div className="spry-drive-picker__item-info">
                    <div className="spry-drive-picker__item-name">{toDisplayTitle(board.title)}</div>
                    <div className="spry-drive-picker__item-meta">
                      Shared by {board.owner?.name || board.owner?.email || "unknown"}
                    </div>
                  </div>
                  <div className="spry-drive-picker__source-badge spry-drive-picker__source-badge--shared">
                    Shared
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
